import _ from 'lodash';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { makeGet, makePatch } from '../../../../../../api/ajax';
import { isSafari } from '../../../../../../utils/browser';

import { ReactComponent as Eye } from '../../../../../../assets/img/icn-eye.svg';
import { ReactComponent as AddressOutlineIcon } from '../../../../../../assets/img/icn-address-outline.svg';
import { ReactComponent as WarningOutlineIcon } from '../../../../../../assets/img/icn-warning-outline.svg';
import { ReactComponent as CommentsOutlineIcon } from '../../../../../../assets/img/icn-comments-outline.svg';
import { ReactComponent as IconYellowWarning } from '../../../../../../assets/img/icn-warning-yellow.svg';
import { ReactComponent as IconQuickActions } from '../../../../../../assets/img/icn-quick-actions.svg';
import { composeLocationApiUrl } from '../../../../../../api/composeApiUrl';
import { useBeforeUnload } from '../../../../../../hooks';
import { selectors as photoSeriesSelectors, actions as photoSeriesActions, helpers as photoSeriesHelpers } from '../../../../../../redux/photoSeries';
import { selectors as damagesSelectors, constants as damagesConstants } from '../../../../../../redux/damages';

import { authenticationService } from '../../../../../../services';
import { constants as sessionConstants } from '../../../../../../redux/session';
import { selectors as imagesSelectors, constants as imagesConstants, useImageTypeErrors } from '../../../../../../redux/images';
import {
  Loader, PhotoViewer, Sidebar, Toggle,
  onHideHandler, renderShowConfirmationModal,
} from '../../../../../../components';

import CommentsSection from './containers/CommentsSection/CommentsSection';
import DamagesSection from './containers/DamagesSection/DamagesSection';
import InlineVinEdit from './containers/InlineVinEdit/InlineVinEdit';
import ImageCompareSearch from './containers/ImageCompareSearch/ImageCompareSearch';
import DeleteImage from './containers/DeleteImage/DeleteImage';

import { useCanViewPhotoSeriesDamages } from '../../hooks/useCanViewPhotoSeriesDamages';

import { formatDateTime } from '../../../../../../utils/date';

const {
  VIEW_WINDSHIELD_DAMAGES_RENDERING,
  VIEW_VEHICLES
} = sessionConstants.PERMISSION_TYPES;

const createShapesFromAnnotations = (annotations) => {
  const shapes = [];
  const invalidDamageShapes = [];

  // Currently render only windshield annotation
  const windshield = annotations.find((a) => a.bodyPart === damagesConstants.BODY_PART.WINDSHIELD);
  const lineWidth = 2;

  // Create windshield annotation shapes
  if (windshield && windshield.subMasks) {
    windshield.subMasks.forEach((subMask) => {
      const { subMaskSubType, subMaskType } = subMask;
      let damageInformationCreated = false;

      subMask.maskParts.forEach((mask) => {
        const points = mask.map((point) => ({ x: point.x, y: point.y }));

        let label;
        let strokeColor;
        const information = [];

        if (subMaskSubType === damagesConstants.SUB_MASK_SUB_TYPE.CRACK && !damageInformationCreated) {
          information.push('Crack');
          damageInformationCreated = true;
        } else if (subMaskSubType === damagesConstants.SUB_MASK_SUB_TYPE.CHIP && !damageInformationCreated) {
          information.push('Chip');
          damageInformationCreated = true;
        }

        switch (subMaskType) {
          case damagesConstants.SUB_MASK_TYPE.REPAIRABLE_AREA:
            label = 'Repairable area';
            strokeColor = '#28a745';
            break;
          case damagesConstants.SUB_MASK_TYPE.NON_REPAIRABLE_AREA:
            label = 'Non-repairable area';
            strokeColor = '#ffc107';
            break;
          case damagesConstants.SUB_MASK_TYPE.INVALID_DAMAGE:
            label = 'Invalid damage';
            strokeColor = '#000000ff';
            break;
          case damagesConstants.SUB_MASK_TYPE.WINDSHIELD_AREA:
            label = 'Windshield area';
            strokeColor = '#007bff';
            break;
          case damagesConstants.SUB_MASK_TYPE.USER_MARKED_DAMAGE:
            label = 'User marked damage';
            strokeColor = '#dc3545';
            break;
          case damagesConstants.SUB_MASK_TYPE.AI_DETECTED_DAMAGE:
            label = 'AI detected damage';
            strokeColor = '#00e5ffff';
            break;
          case damagesConstants.SUB_MASK_TYPE.SEGMENT_SEARCH_AREA:
            label = 'Segment search area';
            strokeColor = '#6f42c1';
            break;
          default:
            label = 'Unknown area';
            strokeColor = '#6c757d';
        }

        const shape = {
          points,
          label,
          style: {
            strokeColor,
            lineWidth,
          },
          information
        };

        // Add invalid damage shapes to separate array to render them last
        if (subMaskType === damagesConstants.SUB_MASK_TYPE.INVALID_DAMAGE) {
          invalidDamageShapes.push(shape);
        } else {
          shapes.push(shape);
        }
      });
    });
  }

  // Append invalid damage shapes at the end so they're painted last
  return [...shapes, ...invalidDamageShapes];
};

const ImageDetails = ({ image, onSwitchPhoto, onClose }) => {
  const dispatch = useDispatch();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [compareTargetSrc, setCompareTargetSrc] = useState(null);
  const [render, setRender] = useState(false);
  const [shapes, setShapes] = useState(null);

  const zoomWrapperRef = useRef(null);

  const photoSeries = useSelector(photoSeriesSelectors.selectPhotoSeries)[image.photoSeriesId];
  const imageLocalUrls = useSelector(imagesSelectors.selectImageLocalUrls);

  const gpsEnabled = photoSeries?.trackGeolocationEnabled;
  const gpsMissingAndEnabled = image?.location === null && gpsEnabled;

  const canViewPhotoSeriesDamages = useCanViewPhotoSeriesDamages(image.photoSeriesId);
  const damages = useSelector((state) => damagesSelectors.selectImageDamages(state, image.photoSeriesId, image.id));
  const allDamages = useSelector((state) => damagesSelectors.selectPhotoSeriesDamages(state, image.photoSeriesId));
  const imageTypeErrors = useImageTypeErrors();

  useEffect(() => {
    if (render) {
      makeGet(`annotations/${image.id}/windshield`).subscribe(({ response }) => {
        const annotations = response?.map((annotation) => ({
          bodyPart: annotation.bodyPart,
          subMasks: annotation.subMasks
        })) || [];

        const shapes = createShapesFromAnnotations(annotations);
        setShapes(shapes);
      });
    }

    setShapes(null);
  }, [render, image.id]);

  useBeforeUnload(hasUnsavedChanges);

  const imageLocalUrl = image && imageLocalUrls && imageLocalUrls[image.id];

  const handleSelectPhoto = useCallback((nextPhoto) => {
    if (zoomWrapperRef.current) zoomWrapperRef.current.reset();
    setZoom(1);
    onSwitchPhoto(nextPhoto);
    setCompareTargetSrc(null);
  }, [zoomWrapperRef, setZoom, onSwitchPhoto]);

  const handleClose = useCallback(() => {
    onHideHandler(hasUnsavedChanges, onClose, setShowConfirmModal);
  }, [hasUnsavedChanges, onClose, setShowConfirmModal]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') return;

      if (event.keyCode === 37) {
        // left arrow
        handleSelectPhoto(-1);
      }
      else if (event.keyCode === 39) {
        // right arrow
        handleSelectPhoto(1);
      }
      else if (event.keyCode === 27) {
        // escape
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSelectPhoto, handleClose]);

  const getImageName = (image) => {
    let name = `${imagesConstants.CAPTURE_NAMES[image.imageType]} capture`;
    if (image.imageSubType) name += `: ${imagesConstants.SUBTYPE_NAMES[image.imageSubType]}`;
    if (image.customImageSubType) {
      name += `: ${image.customImageSubType.name}`;
    }
    return name;
  };

  const withoutVisibilities = (arr) => _.filter(
    arr,
    (e) => imagesConstants.IMAGE_TYPE_VISIBILITY_ERRORS[imagesConstants.IMAGE_TYPES.EXTERIOR].indexOf(e.error) === -1
  );

  const onlyImageTypeErrors = (arr) => _.filter(arr, (e) => imageTypeErrors.indexOf(e.error) > -1);

  const renderImageErrors = (imageErrors) => {
    if (imageErrors?.length === 0) {
      return 'No errors found';
    }

    return (
      <ul>
        {
          onlyImageTypeErrors(withoutVisibilities(imageErrors)).map((errorDto, index) => {
            const errorText = imagesConstants.IMAGE_VERIFICATION_ERROR_DISPLAY_TEXT[errorDto.error];
            return (
              <li key={index.toString()}>
                <div className="row">
                  <div className="col">
                    <IconYellowWarning className="mr-2" />
                    {
                      errorDto.errorDetails && errorDto.errorDetails.length > 0
                        ? <span className="tooltip-on-hover" data-tooltip={errorDto.errorDetails}>{errorText}</span>
                        : <span>{errorText}</span>
                    }
                  </div>
                </div>
              </li>
            );
          })
        }
      </ul>
    );
  };

  const { hasPermission } = authenticationService;

  const visibilityError = imagesConstants.IMAGE_VERIFICATION_ERROR_DISPLAY_TEXT[image.visibilityDetectionResult?.value];
  const confidenceTooltip = `Confidence: ${Math.fround(image.visibilityDetectionResult?.confidence * 100).toFixed(2)}%`;
  const dirtyText = (
    <div className="tooltip-on-hover" data-tooltip={confidenceTooltip}>
      <span className={visibilityError ? 'average-rating ml-1' : 'high-rating ml-1'}>
        {visibilityError || 'Clean'}
      </span>
    </div>);

  const renderCloseupDamageName = () => {
    if (image.imageType !== imagesConstants.IMAGE_TYPES.ADDITIONAL) return '';
    if (!image.relatedObjects || image.relatedObjects.length === 0) return '';

    const relatedObject = image.relatedObjects[0];
    const damage = allDamages.find((d) => d.damageId === relatedObject.id);
    if (!damage) return '';
    if (!damage.customDamageType) return ` - ${damagesConstants.getDamageTypeName(damage)}`;

    return ` - ${damage.customDamageType.name}`;
  };

  const isAdditionalImage = image.imageType === imagesConstants.IMAGE_TYPES.ADDITIONAL;
  const isVin = image.imageType === imagesConstants.IMAGE_TYPES.VIN;
  const isWindshieldFromOutside = image.imageType === imagesConstants.IMAGE_TYPES.WINDSHIELD
    && image.imageSubType === imagesConstants.IMAGE_SUB_TYPES.FROM_OUTSIDE;

  const hasWindshieldRepairEstimation = (isWindshieldFromOutside || isAdditionalImage)
    && photoSeries?.repairEstimations?.some((re) => re.bodyPart === damagesConstants.BODY_PART.WINDSHIELD);

  const canShowRenderToggle = hasPermission(VIEW_WINDSHIELD_DAMAGES_RENDERING)
    && hasWindshieldRepairEstimation;

  const handleSaveVin = (data, onSuccess, onError) => {
    makePatch(`photoSeries/${image.photoSeriesId}`, data).subscribe(
      ({ response }) => {
        dispatch(photoSeriesActions.loadPhotoSeriesSuccess(image.photoSeriesId, response));
        if (onSuccess) onSuccess('VIN has been updated!');
      },
      ({ response: { error } }) => { if (onError) onError(photoSeriesHelpers.getVinErrorMessage(error)); },
    );
  };

  return (
    <>
      {renderShowConfirmationModal(showConfirmModal, setShowConfirmModal, () => {
        onClose();
        setShowConfirmModal(false);
      })}
      <PhotoViewer
        src={imageLocalUrl}
        compareTargetSrc={compareTargetSrc}
        onNextClick={() => handleSelectPhoto(1)}
        onPrevClick={() => handleSelectPhoto(-1)}
        onZoomChange={(value) => setZoom(value)}
        zoom={zoom}
        zoomWrapperRef={zoomWrapperRef}
        damages={canViewPhotoSeriesDamages() ? damages.filter((damage) => damage.show) : []}
        shapes={shapes}
      >
        <Sidebar
          title={`${getImageName(image)}${renderCloseupDamageName()}`}
          subTitle={`Captured: ${formatDateTime(image.createdAt)}`}
          onClose={handleClose}
          hidden={sidebarHidden}
          onToggleHideClick={() => setSidebarHidden(!sidebarHidden)}
          additionalActions={
            <div className="d-flex justify-content-start align-items-center w-100">
              {
                image.imageType !== imagesConstants.IMAGE_TYPES.ADDITIONAL && !isSafari() && hasPermission(VIEW_VEHICLES) && (
                  <ImageCompareSearch
                    image={image}
                    setCompareTargetSrc={setCompareTargetSrc}
                  />
                )
              }
              <DeleteImage imageId={image.id} />
            </div>
          }
        >
          {(canShowRenderToggle || isVin) && (
            <Sidebar.Section>
              <Sidebar.Section.Header text="Quick actions">
                <IconQuickActions />
              </Sidebar.Section.Header>
              <Sidebar.Section.Body>
                {isVin && (
                  <InlineVinEdit
                    vin={photoSeries?.vehicle?.vin}
                    onSave={handleSaveVin}
                  />
                )}
                {canShowRenderToggle && (
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <span>Render AI lines</span>
                    <Toggle on={render} toggleClick={() => setRender(!render)} />
                  </div>
                )}
              </Sidebar.Section.Body>
            </Sidebar.Section>
          )}
          {(image?.location?.latitude || gpsEnabled) && (
            <Sidebar.Section>
              <Sidebar.Section.Header text="Location">
                <AddressOutlineIcon />
              </Sidebar.Section.Header>
              <Sidebar.Section.Body>
                {!gpsMissingAndEnabled
                  ? (
                    <a
                      href={composeLocationApiUrl(image.location)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="value map"
                    >
                      View on Google Maps
                    </a>
                  ) : (<span>GPS validation required but not available</span>)}
              </Sidebar.Section.Body>
            </Sidebar.Section>
          )}
          <Sidebar.Section className="error-list">
            <Sidebar.Section.Header text="Errors">
              <WarningOutlineIcon />
            </Sidebar.Section.Header>
            <Sidebar.Section.Body>
              {renderImageErrors(image.errors)}
            </Sidebar.Section.Body>
          </Sidebar.Section>
          <Sidebar.Section className="d-flex flex-column flex-grow-1 overflow-hidden">
            {image.visibilityDetectionResult
            && (
              <Sidebar.Section.Header text={<>Visibility: {dirtyText}</>}>
                <Eye className="svg-md" />
              </Sidebar.Section.Header>
            )}
          </Sidebar.Section>
          {
            canViewPhotoSeriesDamages(false) && damages && damages.length > 0 && (
              <DamagesSection
                photoSeriesId={image.photoSeriesId}
                imageId={image.id}
                damages={damages}
                render={render}
              />
            )
          }
          {hasPermission(sessionConstants.PERMISSION_TYPES.VIEW_COMMENTS) && (
            <Sidebar.Section className="d-flex flex-column flex-grow-1 overflow-hidden">
              <Sidebar.Section.Header text="Comments">
                {commentsLoading
                  ? <Loader size="tiny" isRelative noDelay />
                  : <CommentsOutlineIcon />}
              </Sidebar.Section.Header>
              <Sidebar.Section.Body className="d-flex flex-column flex-grow-1 overflow-hidden">
                <CommentsSection
                  photoSeriesId={image.photoSeriesId}
                  imageId={image.id}
                  hasUnsavedChanges={hasUnsavedChanges}
                  setHasUnsavedChanges={setHasUnsavedChanges}
                  setLoading={setCommentsLoading}
                />
              </Sidebar.Section.Body>
            </Sidebar.Section>
          )}
        </Sidebar>
      </PhotoViewer>
    </>
  );
};

ImageDetails.propTypes = {
  image: PropTypes.object.isRequired,
  onSwitchPhoto: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ImageDetails;
